import type { CaptureResult } from "posthog-js";

// These are route candidates, NOT a claim that a component caused the mismatch.
// Next's production recoverable-error callback discards React's component stack.
// Do not patch that private callback or inspect rendered text/HTML to recover it.
const SCREENS = [
  { route: /^\/d\/project-parents\/[^/]+\/media\/?$/, category: "project-media", component: "MediaLibraryClient" },
  { route: /^\/d\/project-parents\/[^/]+\/finance\/quotations\/?$/, category: "project-quotations", component: "QuotationsClient" },
  { route: /^\/d\/project-parents\/[^/]+\/finance\/quotations\/[^/]+\/?$/, category: "project-quotation", component: "QuotationClient" },
  { route: /^\/x\/activity\/?$/, category: "china-activity", component: "XActivityClient" },
  { route: /^\/d\/activity\/?$/, category: "internal-activity", component: "ActivityClient" },
] as const;

function isTextHydrationError(value: unknown): boolean {
  // Match only React's production #418 text mismatch, not arbitrary messages
  // mentioning hydration. Inspect in memory; never copy the message into detail.
  return typeof value === "string" &&
    /^Minified React error #418; visit https:\/\/react\.dev\/errors\/418\?args\[\]=text(?:&|$)/.test(value);
}

/** Add fixed, low-cardinality diagnostic labels to an EXISTING exception only. */
export function addHydrationDiagnostics(event: CaptureResult | null): CaptureResult | null {
  if (!event || event.event !== "$exception") return event;
  const exceptions: unknown = event.properties.$exception_list;
  if (!Array.isArray(exceptions) || !exceptions.some((exception: unknown) =>
    exception !== null && typeof exception === "object" &&
    "value" in exception && isTextHydrationError(exception.value)
  )) return event;

  // Use the event's pathname, not window.location (which may have navigated).
  // Match only; never emit path segments, query/hash, IDs or unknown values.
  const pathname: unknown = event.properties.$pathname;
  const path = typeof pathname === "string" && pathname.length <= 2048
    ? pathname.split(/[?#]/, 1)[0] : "";
  const screen = SCREENS.find(({ route }) => route.test(path));
  const shell = /^\/(?:d|x)(?:\/|$)/.test(path);
  return {
    ...event,
    properties: {
      ...event.properties,
      hydration_diagnostic_version: 1,
      hydration_mismatch: "text",
      hydration_route_category: screen?.category ?? (path === "/d" || path === "/x" ? "portal-home" : "other"),
      hydration_component_candidates: screen
        ? ["AppSidebar", screen.component] : shell ? ["AppSidebar"] : [],
      hydration_component_source: "route-map-not-error-stack",
    },
  };
}
